import { FC, useCallback, useEffect, useRef, useState } from "react";
import { registerLocale } from "react-datepicker";
import { useDispatch } from "react-redux";
import DatePicker, { DateObject, DatePickerRef } from "react-multi-date-picker";
import clsx from "clsx";

import { startOfMonth, subMonths } from "date-fns";
import ru from "date-fns/locale/ru";
import moment from "moment";

import { AppDispatch } from "../../../redux/reducer/store";
import { formatInputsDate, stateDate } from "../../../utils/formatDate";

import CustomDateInput from "../customDateInput/customDateInput";
import OptionsComponent from "./optionsComponent";
import ButtonContainer from "./buttons";

import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar.svg";

import "react-date-range/dist/theme/default.css";
import "react-date-range/dist/styles.css";
import "./rangeDatePicker.scss";
import { groupList } from "../../../utils/chartsData";

interface IRangeDatePickerProps {
  defaultDateFrom?: string;
  setInputValues?: any;
  setCurrentPage?: any;
  defaultDateTo?: string;
  setStartPage?: any;
  inputValues?: any;
  reducer?: any;
  action?: any;
  limit?: number;
  page?: number;
  setLoading?: any;
  placeholder?: string;
  pageName?: string;
  name?: string;
  id?: number | string;
}

const RangeDatePicker: FC<IRangeDatePickerProps> = ({
  defaultDateFrom,
  setCurrentPage,
  defaultDateTo,
  setStartPage,
  inputValues,
  placeholder,
  setLoading,
  pageName,
  reducer,
  action,
  limit,
  page,
  name,
  id,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [isClickOnAcceptBtn, setIsClickOnAcceptBtn] = useState<boolean>(false);
  const [isClickOnClearBtn, setIsClickOnClearBtn] = useState<boolean>(false);
  const [isOpenDateBlock, setIsOpenDateBlock] = useState<boolean>(false);
  const [isHiddenMenu, setIsHiddenMenu] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [date, setDate] = useState<string[] | Date[]>([
    inputValues.fromDate,
    inputValues.toDate,
  ]);
  const [dateInputValues, setDateInputValues] = useState<any>([
    defaultDateFrom,
    defaultDateTo,
  ]);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [isCountCalendar, setIsCountCalendar] = useState<number>(2);

  const actualLimit =
    JSON.parse(localStorage.getItem("pageLimit") as string) || 10;
  const selectRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<DatePickerRef>();

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  const handleClickAcceptBtn = useCallback(() => {
    setIsClickOnAcceptBtn(true);
    setCurrentPage(1);
    setIsOpen(false);
    setIsOpenDateBlock(false);
    setStartPage(1);
    handleClose();
  }, [setCurrentPage, setStartPage]);

  useEffect(() => {
    registerLocale("ru", ru);
  }, []);

  useEffect(() => {
    if (isClickOnAcceptBtn) {
      const formattedDateFrom = moment(date && date[0]).format("YYYY-MM-DD");
      const formattedDateTo = moment(date[1] ? date[1] : date[0]).format(
        "YYYY-MM-DD"
      );

      handleDateUpdate(formattedDateFrom, formattedDateTo);
      setIsClickOnAcceptBtn(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClickOnAcceptBtn]);

  useEffect(() => {
    if (isClickOnClearBtn) {
      const fromDate = stateDate(startOfMonth(subMonths(new Date(), 1)));
      const toDate = stateDate(new Date());
      setDateRange(startOfMonth(subMonths(new Date(), 1)), new Date());
      handleDateUpdate(fromDate, toDate);
      setIsClickOnClearBtn(false);
      setCurrentPage(1);
      setStartPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClickOnClearBtn]);

  useEffect(() => {
    const handleClickOutsideModal = (e: MouseEvent) => {
      if (
        !datePickerRef.current?.contains(e.target as Node) &&
        !selectRef.current?.contains(e.target as Node)
      ) {
        handleClose();
        setIsOpenDateBlock(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideModal);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideModal);
    };
  }, [handleClickAcceptBtn]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (windowWidth <= 990) {
      setIsCountCalendar(1);
      if (windowWidth <= 560) {
        setIsHiddenMenu(isOpenDateBlock);
      } else if (windowWidth > 560 && isOpenDateBlock) {
        setIsHiddenMenu(false);
      }
    } else {
      setIsCountCalendar(2);
    }
  }, [windowWidth, isOpenDateBlock]);

  useEffect(() => {
    if (isOpenDateBlock) {
      setDateInputValues([defaultDateFrom, defaultDateTo]);
    }
  }, [defaultDateFrom, defaultDateTo, isOpenDateBlock]);

  const handleDateUpdate = (fromDate: string, endDate: string) => {
    const toDate =
      new Date(endDate) > new Date() ? stateDate(new Date()) : endDate;

    const newInputValues =
      pageName === "applicationList"
        ? {
            ...inputValues,
            modifiedDate: {
              from: fromDate,
              to: toDate,
            },
          }
        : pageName === "vacancyList"
        ? {
            ...inputValues,
            [name as string]: {
              from: fromDate,
              to: toDate,
            },
          }
        : {
            ...inputValues,
            fromDate,
            toDate,
          };
    if (id || id === 0) {
      if (pageName === "charts") {
        dispatch(reducer({ id, value: { fromDate, toDate } }));
        setLoading(true);
      } else if (pageName === "actions") {
        setLoading(true);
        dispatch(reducer({ ...newInputValues }));
        dispatch(action(id, groupList, true)).then(() => {
          setLoading(false);
        });
      } else {
        dispatch(reducer({ id, data: newInputValues }));
        setLoading(true);
        dispatch(action(id)).then(() => {
          setLoading(false);
        });
      }
    } else {
      dispatch(reducer(newInputValues));
      setLoading(true);
      dispatch(action(actualLimit ? actualLimit : limit, page)).then(() => {
        setLoading(false);
      });
    }
  };

  const setDateRange = (startDate: Date, endDate: Date) => {
    setDate([startDate, endDate]);
    setDateInputValues([
      formatInputsDate(startDate),
      formatInputsDate(endDate),
    ]);
  };

  const handleOpenModal = () => {
    if (!isOpen) {
      setIsOpen(true);
      datePickerRef.current?.openCalendar();
    } else {
      handleClose();
    }
  };

  const handleDatePickerChange = (date: any) => {
    const formattedStartDate = new DateObject(date[0]).format("YYYY-MM-DD");
    const formattedEndDate = date[1]
      ? new DateObject(date[1]).format("YYYY-MM-DD")
      : "";
    const formattedStartDateInputs = new DateObject(date[0]).format(
      "DD/MM/YYYY"
    );
    const formattedEndDateInputs = date[1]
      ? new DateObject(date[1]).format("DD/MM/YYYY")
      : "";
    setDate([formattedStartDate, formattedEndDate]);
    setDateInputValues([
      formattedStartDateInputs,
      formattedEndDateInputs === ""
        ? formattedStartDateInputs
        : formattedEndDateInputs,
    ]);
  };

  const handleMouseEnter = (hovered: any) => {
    setHoveredDate(hovered);
  };

  const handleMouseLeave = () => {
    setHoveredDate(null);
  };

  const isWithinRange = (currentDate: any) => {
    if (date[1] && date[1] !== "") return false;

    if (date.length > 0 && hoveredDate) {
      const startDate = new Date(date[0]);
      const hoverDateObject = new Date(hoveredDate);
      const currentDateObject = new Date(currentDate);

      if (hoverDateObject > startDate) {
        return (
          currentDateObject >= startDate && currentDateObject <= hoverDateObject
        );
      } else {
        return (
          currentDateObject >= hoverDateObject && currentDateObject <= startDate
        );
      }
    }
    return false;
  };

  const handleClose = () => {
    datePickerRef.current?.closeCalendar();
    setIsOpen(false);
  };

  const handleClickClearBtn = () => {
    setDate([startOfMonth(subMonths(new Date(), 1)), new Date()]);
    setDateInputValues([
      formatInputsDate(startOfMonth(subMonths(new Date(), 1))),
      formatInputsDate(new Date()),
    ]);
    setIsClickOnClearBtn(true);
    setIsOpenDateBlock(false);
    setCurrentPage(1);
    setStartPage(1);
    handleClose();
  };

  return (
    <div className="wrapper">
      <DatePicker
        ref={datePickerRef}
        dateSeparator={" "}
        value={date}
        onChange={handleDatePickerChange}
        range={true}
        numberOfMonths={isCountCalendar}
        weekDays={["ВС", "ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ"]}
        months={[
          "Январь",
          "Февраль",
          "Март",
          "Апрель",
          "Май",
          "Июнь",
          "Июль",
          "Август",
          "Сентябрь",
          "Октябрь",
          "Ноябрь",
          "Декабрь",
        ]}
        showOtherDays={true}
        weekStartDayIndex={1}
        shadow={false}
        highlightToday={false}
        className={`custom-calendar ${
          !isOpenDateBlock ? "custom-calendar_hidden" : ""
        }`}
        monthYearSeparator={" "}
        maxDate={new Date()}
        mapDays={({ date }) => {
          const isHighlighted = isWithinRange(date);
          return {
            onMouseEnter: () => {
              handleMouseEnter(date);
            },
            onMouseLeave: () => handleMouseLeave(),
            className: isHighlighted ? "hover-highlight" : "",
          };
        }}
        plugins={[
          <OptionsComponent
            setIsHiddenMenu={setIsHiddenMenu}
            handleClickAcceptBtn={handleClickAcceptBtn}
            setDateInputValues={setDateInputValues}
            handleClickClearBtn={handleClickClearBtn}
            setIsOpenDateBlock={setIsOpenDateBlock}
            defaultDateFrom={defaultDateFrom}
            setCurrentPage={setCurrentPage}
            defaultDateTo={defaultDateTo}
            isHiddenMenu={isHiddenMenu}
            setStartPage={setStartPage}
            setDateRange={setDateRange}
            inputValues={inputValues}
            placeholder={placeholder}
            setLoading={setLoading}
            pageName={pageName}
            reducer={reducer}
            position={"left"}
            action={action}
            limit={limit}
            page={page}
            name={name}
            date={date}
            id={id}
          />,
          <CustomDateInput
            setDateInputValues={setDateInputValues}
            dateInputValues={dateInputValues}
            isCountCalendar={isCountCalendar}
            setDate={setDate}
            date={date}
            position={"top"}
          />,
          <ButtonContainer
            visible={isHiddenMenu && isOpenDateBlock}
            handleClickAcceptBtn={handleClickAcceptBtn}
            handleClickClearBtn={handleClickClearBtn}
            position={"bottom"}
          />,
        ]}
      />
      <div
        className={clsx(
          "input__date_wrapper",
          pageName === "charts" || pageName === "actions"
            ? "input__date_wrapper_forCharts"
            : ""
        )}
        style={{ minWidth: `${pageName === "actions" ? "280px" : "100%"}` }}
        onClick={handleOpenModal}
        ref={selectRef}
        id="date"
      >
        {placeholder && (
          <span
            className={clsx(
              "date__span date__span__mobile",
              name === "startDate" && "date__span_lh"
            )}
          >
            {placeholder}
          </span>
        )}
        <div className="calendar__icon">
          <CalendarIcon width={15} height={15} />
        </div>
        <div className="date__column_block">
          <span className="date__span">{defaultDateFrom}</span>
          <span className="date__span">{defaultDateTo}</span>
        </div>
      </div>
    </div>
  );
};

export default RangeDatePicker;
